import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { useSearchParams } from 'react-router-dom';
import { Box, Text } from 'grommet';
import NoAuthLayout from '@Components/Layout/NoAuth';
import { AppButton, Spinning, ErrorNotice } from '@Components/Control';
import { ProductFormSingleTextInput } from '@Components/Partial/Product/NewSearch';
import { useAppTheme } from '@Components/Context';
import {
  clearSubmitOnboardingRequest,
  initiateProvisionOnboardingRequest,
  clearProvisionOnboardingRequest,
  updatePasswordRequest,
} from '@Actions';
import { paths } from '@Components/configs';

const LOCAL_ONBOARDING_ID_KEY = 'onboardingId';
const PASSWORD_MIN_LENGTH = 7;
const PASSWORD_MIN_SPECIAL = 1;
const SPECIAL_CHAR_REGEX = /[^A-Za-z0-9]/g;

const AccountSetupPage = ({
  small,
  submissionLoading,
  initiateProvision,
  updatePassword,
  clearSubmitOnboarding,
  clearProvisioningResponse,
  onboardingSubmissionResponse = null,
  onboardingProvisioningResponse = null,
}) => {
  const [password, setPassword] = React.useState('');
  const [confirmPassword, setConfirmPassword] = React.useState('');
  const [error, setError] = React.useState('');
  const [provisioned, setProvisioned] = React.useState(false);

  const { theme: { page, button }, noAuthPages } = useAppTheme();
  const [searchParams] = useSearchParams();
  const paramsOnboardingId = searchParams.get('onboardingId');

  // Get onboardingId from state, localStorage or URL params as backup
  const onboardingId = onboardingSubmissionResponse?.onboardingId
    || window.localStorage.getItem(LOCAL_ONBOARDING_ID_KEY)
    || paramsOnboardingId;

  React.useEffect(() => {
    if (!onboardingId) {
      setError('Missing onboarding session. Please complete the signup process.');
      return;
    }

    if (!onboardingProvisioningResponse && !submissionLoading && !error) {
      initiateProvision(onboardingId, setError);
    }
  }, [onboardingId, onboardingProvisioningResponse, initiateProvision]);

  React.useEffect(() => {
    if (onboardingProvisioningResponse) {
      setProvisioned(true);
    }
  }, [onboardingProvisioningResponse]);

  const submitDisabled = submissionLoading
    || !password
    || !confirmPassword
    || password !== confirmPassword;
  const needsPayment = !!onboardingProvisioningResponse?.provisioning?.paymentLink
    && onboardingProvisioningResponse?.provisioning?.paymentPending;
  const userEmail = onboardingProvisioningResponse?.provisioning?.primaryUser?.email;

  // Password validation helper
  const validatePassword = (pwd) => {
    if (!pwd || pwd.length < PASSWORD_MIN_LENGTH) return false;
    const specialMatches = pwd.match(SPECIAL_CHAR_REGEX);
    return specialMatches && specialMatches.length >= PASSWORD_MIN_SPECIAL;
  };

  // Password submit handler
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitDisabled) return;

    // Validate password
    if (!validatePassword(password)) {
      setError(
        `Password must contain at least ${PASSWORD_MIN_LENGTH} characters with ${PASSWORD_MIN_SPECIAL} special character${PASSWORD_MIN_SPECIAL > 1 ? 's' : ''}.`,
      );
      return;
    }

    await updatePassword(
      password,
      onboardingProvisioningResponse?.resetPasswordToken,
      paths.login,
      'Password set successfully, welcome to the platform!',
      onboardingProvisioningResponse?.provisioning?.primaryUser?.email,
      () => {
        // After action success: cleanup state and local storage, user redirected to login
        clearSubmitOnboarding();
        clearProvisioningResponse();
        window.localStorage.removeItem(LOCAL_ONBOARDING_ID_KEY);
      },
    );
  };

  // Render page contents based on state
  const renderPageContents = () => {
    // Fatal error: missing onboardingId or provisioning failed
    if (!onboardingId || (error && !provisioned)) {
      return (
        <Box flex width="100%" align="center" justify="start" gap="1.25rem">
          <Box flex width="100%" justify="center">
            <ErrorNotice
              message={error || 'Could not find onboarding session. Please complete the signup process.'}
              messageColor="var(--app-error)"
              iconColor="var(--app-error)"
              backgroundColor="#FCF8EF"
            />
          </Box>
          <AppButton
            overrideHover
            level="authSubmit"
            bgColor={button.primaryBg}
            color={button.primaryText}
            href={paths.customerOnboarding}
            label="Back to Signup"
            alignSelf="center"
          />
        </Box>
      );
    }

    // Still loading/provisioning
    if ((submissionLoading || !provisioned)) {
      return (
        <Box flex width="100%" align="center" justify="center" gap="1.25rem">
          <Spinning size="large" color={page.highlight} />
          <Text size="1rem" color={page.primaryText}>
            Please wait while we finish getting your account ready.
            You will be prompted to set your password once this process is complete.
            Please do not refresh or close the page.
          </Text>
        </Box>
      );
    }

    // Show password setup form (with error message if present)
    return (
      <Box
        align="center"
        justify="center"
        gap="1.25rem"
        width="100%"
      >
        {needsPayment && (
          <Box width="100%" align="start" gap="1.25rem">
            <Text size="1rem" color={page.primaryText}>
              You can complete your payment and fully activate your account by clicking&nbsp;
              <a
                href={onboardingProvisioningResponse?.provisioning?.paymentLink}
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: page.focusHighlight, textDecoration: 'underline' }}
              >
                here
              </a>
              .
            </Text>
            <Text size="1rem" color={page.primaryText}>
              If you choose to skip this step, you will be able to create your password and log in
              but will not have full platform access. The payment link will remain active,
              so you can complete this step at any time to unlock all account features.
            </Text>
            <Text size="1rem" color={page.primaryText}>
              The payment link was also emailed to you at&nbsp;
              <span style={{ color: page.focusHighlight, fontWeight: 600 }}>
                {userEmail}
              </span>
              .
            </Text>
          </Box>
        )}
        {error && (
          <ErrorNotice
            message={error}
            messageColor="var(--app-error)"
            iconColor="var(--app-error)"
            backgroundColor="#FCF8EF"
          />
        )}
        <ProductFormSingleTextInput
          type="password"
          name="password"
          autoComplete="new-password"
          focusHighlight={page.focusHighlight}
          placeholder={`Password (min ${PASSWORD_MIN_LENGTH} characters, ${PASSWORD_MIN_SPECIAL} special character${PASSWORD_MIN_SPECIAL > 1 ? 's' : ''})`}
          value={password}
          handleFormValues={setPassword}
          style={{ color: page.primaryText }}
        />
        <ProductFormSingleTextInput
          type="password"
          name="confirmPassword"
          autoComplete="new-password"
          focusHighlight={page.focusHighlight}
          placeholder="Confirm password"
          value={confirmPassword}
          handleFormValues={setConfirmPassword}
          style={{ color: page.primaryText }}
        />
        <AppButton
          overrideHover
          level="authSubmit"
          bgColor={button.primaryBg}
          color={button.primaryText}
          label="Set Password"
          alignSelf="center"
          onClick={(e) => handleSubmit(e)}
          disabled={submitDisabled}
        />
      </Box>
    );
  };

  const selectSubTitle = () => {
    if (error && !provisioned) {
      return 'There was an issue setting up your account.';
    }

    if (submissionLoading || !provisioned) {
      return 'Finalizing account setup...';
    }

    return needsPayment ? 'Set your password and complete payment.' : 'Set your password to complete setup.';
  };

  return (
    <NoAuthLayout
      altHeaderStyle
      removeLowerPad
      small={small}
      seoConfig={null}
      bgColor={noAuthPages.bgColor}
      bgImage={noAuthPages.bgImage}
      textColor={noAuthPages.textColor}
      title="Account Setup"
      subTitle={selectSubTitle()}
      justifyContent="center"
      customWidth={small ? '100%' : '35rem'}
    >
      {renderPageContents()}
    </NoAuthLayout>
  );
};

AccountSetupPage.propTypes = {
  small: PropTypes.bool.isRequired,
  onboardingSubmissionResponse: PropTypes.object,
  onboardingProvisioningResponse: PropTypes.object,
  submissionLoading: PropTypes.bool.isRequired,
  initiateProvision: PropTypes.func.isRequired,
  updatePassword: PropTypes.func.isRequired,
  clearSubmitOnboarding: PropTypes.func.isRequired,
  clearProvisioningResponse: PropTypes.func.isRequired,
};

function mapStateToProps(state) {
  return {
    submissionLoading: state.fetchLoader.submissionLoading,
    onboardingSubmissionResponse: state.payment.onboardingSubmissionResponse,
    onboardingProvisioningResponse: state.payment.onboardingProvisioningResponse,
  };
}

function mapDispatchToProps(dispatch) {
  return bindActionCreators({
    initiateProvision: initiateProvisionOnboardingRequest,
    updatePassword: updatePasswordRequest,
    clearSubmitOnboarding: clearSubmitOnboardingRequest,
    clearProvisioningResponse: clearProvisionOnboardingRequest,
  }, dispatch);
}

export default connect(mapStateToProps, mapDispatchToProps)(AccountSetupPage);
